import { toBaseAmount } from '@familywallet/shared';
import { useTranslation } from 'react-i18next';
import { parsePositive } from '../lib/add-form';
import { formatAmount } from '../lib/money';

/**
 * Under an amount input, as the person types: the number written out when it was typed short
 * ("500k" -> "500,000 LBP") and its base-currency equivalent ("≈ $5.59") at the given rate.
 * Nothing when the amount is not valid yet or there is nothing to add.
 */
export function AmountPreview({
  raw,
  currency,
  baseCurrency,
  rate,
}: {
  raw: string;
  currency: string;
  baseCurrency: string;
  /** 1 base = rate; none for the base currency or when the family has no rate. */
  rate: string | null | undefined;
}) {
  const { i18n } = useTranslation();
  const amount = currency ? parsePositive(raw, currency) : null;
  if (!amount) return null;
  // Written short ("500k", "1.5m") or a long number without separators ("1500000").
  const expanded = /[a-z]/i.test(raw) || (!raw.includes(',') && /\d{4}/.test(raw));
  const equivalent =
    currency !== baseCurrency && rate
      ? formatAmount(toBaseAmount(amount, rate), baseCurrency, i18n.language)
      : null;
  if (!expanded && !equivalent) return null;
  return (
    <span dir="ltr" className="text-sm text-muted tabular-nums" data-testid="amount-preview">
      {[
        expanded ? formatAmount(amount, currency, i18n.language) : null,
        equivalent && `≈ ${equivalent}`,
      ]
        .filter(Boolean)
        .join(' ')}
    </span>
  );
}
